import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { MdAdd, MdOutlinePendingActions } from "react-icons/md";

import HeadlessDialog from "@components/Dialog";
import HeadlessButton from "@components/Button";
import ScrollableModalBody from "@components/ScrollableModalBody";
import Spinner from "@components/Spinner";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import {
  createWorkerEngagement,
  fetchEngagementStatus,
  fetchWorkerEngagements,
  updateWorkerEngagementRecord,
} from "@service/contractorService";
import { ISelect } from "@interfaces/ISelect";
import {
  IWorkerEngagementDTO,
  IWorkerEngagementUpsertDTO,
} from "@interfaces/IWorkerEngagementDTO";
import { IEngagementStatusDTO } from "@interfaces/IEngagementStatusDTO";

interface ContractorEngagementStatusModalProps {
  workerId: number;
  contractorId: number;
  contractorName: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const createInitialForm = (): IWorkerEngagementUpsertDTO => ({
  status: "active",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
});

export default function ContractorEngagementStatusModal({
  workerId,
  contractorId,
  contractorName,
  isOpen,
  onClose,
  onSaved,
}: ContractorEngagementStatusModalProps) {
  type OperationMode = "create" | "edit" | null;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusOptions, setStatusOptions] = useState<ISelect[]>([]);
  const [engagements, setEngagements] = useState<IWorkerEngagementDTO[]>([]);
  const [operationMode, setOperationMode] = useState<OperationMode>(null);
  const [selectedEngagement, setSelectedEngagement] =
    useState<IWorkerEngagementDTO | null>(null);
  const [formValues, setFormValues] = useState<IWorkerEngagementUpsertDTO>(
    createInitialForm()
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const breakpoint = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});
  const handleContextMenu = useCallback((event: MouseEvent) => {
    event.preventDefault();
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadData = useCallback(async () => {
    if (!isOpen || !workerId) return;

    setIsLoading(true);
    setError(null);
    try {
      const [statuses, history] = await Promise.all([
        fetchEngagementStatus(),
        fetchWorkerEngagements(workerId),
      ]);

      const mappedStatuses = (statuses ?? []).map(
        (status: IEngagementStatusDTO) => ({
          id: status.id,
          value: status.name,
        })
      );

      setStatusOptions(mappedStatuses);
      setEngagements(Array.isArray(history) ? history : []);

      if (mappedStatuses.length > 0) {
        setFormValues((prev) => ({
          ...prev,
          status: prev.status || mappedStatuses[0].value,
        }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load engagements.");
      setEngagements([]);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, workerId]);

  useEffect(() => {
    if (!isOpen) return;
    setOperationMode(null);
    setSelectedEngagement(null);
    setFormValues(createInitialForm());
    setSuccess(null);
    setError(null);
    loadData();
  }, [isOpen, loadData]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Status",
        field: "status",
        filter: true,
        flex: 1,
      },
      {
        headerName: "Start Date",
        field: "start_date",
        width: 140,
        filter: true,
      },
      {
        headerName: "End Date",
        field: "end_date",
        width: 140,
        filter: true,
        valueFormatter: (params: any) => params.value || "Current",
      },
      // {
      //   headerName: "Current",
      //   field: "is_current",
      //   width: 100,
      //   filter: true,
      //   valueFormatter: (params: any) => (params.value ? "Yes" : "No"),
      // },
    ],
    []
  );

  const handleRowSelected = useCallback((event: any) => {
    const row = event?.data as IWorkerEngagementDTO;
    if (!row) return;

    setOperationMode("edit");
    setSelectedEngagement(row);
    setFormValues({
      status: row.status,
      start_date: row.start_date || "",
      end_date: row.end_date || "",
    });
    setSuccess(null);
    setError(null);
  }, []);

  const startCreateMode = useCallback(() => {
    setOperationMode("create");
    setSelectedEngagement(null);
    setFormValues({
      ...createInitialForm(),
      start_date: "",
    });
    setSuccess(null);
    setError(null);
  }, []);

  const clearOperation = useCallback((clearMessages = true) => {
    setOperationMode(null);
    setSelectedEngagement(null);
    setFormValues(createInitialForm());
    if (clearMessages) {
      setSuccess(null);
      setError(null);
    }
  }, []);

  const isEditingCurrentEngagement = Boolean(selectedEngagement?.is_current);

  const handleSave = useCallback(async () => {
    setError(null);
    setSuccess(null);

    if (!operationMode) {
      setError("Select Create New or an engagement row before saving.");
      return;
    }

    if (!formValues.status) {
      setError("Status is required.");
      return;
    }

    if (!formValues.start_date) {
      setError("Start date is required.");
      return;
    }

    setIsSaving(true);
    try {
      if (operationMode === "edit") {
        if (!selectedEngagement) {
          setError("No engagement selected for editing.");
          return;
        }

        await updateWorkerEngagementRecord(selectedEngagement.id, {
          status: formValues.status,
          start_date: formValues.start_date,
          end_date: formValues.end_date || null,
        });
        setSuccess("Engagement updated successfully.");
      } else {
        await createWorkerEngagement(workerId, {
          status: formValues.status,
          start_date: formValues.start_date,
        });
        setSuccess("Engagement created successfully.");
      }

      await loadData();
      onSaved();
      clearOperation(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to save engagement.");
    } finally {
      setIsSaving(false);
    }
  }, [
    operationMode,
    formValues,
    selectedEngagement,
    workerId,
    loadData,
    onSaved,
    clearOperation,
  ]);

  if (!isOpen) return null;

  return (
    <HeadlessDialog
      title={
        <div className="tw-flex tw-items-center tw-gap-2">
          <MdOutlinePendingActions
            className="tw-text-primary-light"
            size={24}
          />
          <span>{`Status - ${contractorName} (Contractor ID: ${contractorId})`}</span>
        </div>
      }
      description="Manage engagement statuses for this contractor"
      isOpen={isOpen}
      setIsOpen={(open: boolean) => {
        if (!open) onClose();
      }}
      onClose={onClose}
      buttonText="Close"
      size="tw-w-[100vw] xl:tw-max-w-[60vw]"
    >
      {isLoading ? (
        <div className="tw-p-6">
          <Spinner className="tw-flex tw-justify-center tw-items-center" />
        </div>
      ) : (
        <ScrollableModalBody className="tw-overflow-y-scroll">
          <div className="tw-space-y-2 tw-ml-[2px]">
            <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
                  Engagement History
                </h3>
                <button
                  type="button"
                  className="tw-hidden md:tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                  onClick={startCreateMode}
                >
                  <MdAdd size={16} />
                  Create New
                </button>
              </div>

              <div className="tw-w-full">
                {!isMobile ? (
                  <div
                    className="ag-theme-balham tw-h-[23dvh]"
                    onContextMenu={handleContextMenu}
                  >
                    <AgGridReact
                      rowData={engagements}
                      columnDefs={columnDefs}
                      rowSelection="single"
                      animateRows
                      onCellContextMenu={handleCellContextMenu}
                      onRowClicked={handleRowSelected}
                      defaultColDef={{ sortable: true, resizable: true }}
                    />
                    {ContextMenuComponent}
                  </div>
                ) : (
                  <div className="tw-max-h-[42dvh] tw-overflow-y-auto tw-space-y-2">
                    {engagements.length > 0 ? (
                      engagements.map((engagement) => {
                        const isSelected =
                          selectedEngagement?.id === engagement.id;
                        return (
                          <button
                            key={engagement.id}
                            type="button"
                            className={`tw-w-full tw-text-left tw-border tw-rounded-lg tw-p-3 tw-bg-white tw-transition ${
                              isSelected
                                ? "tw-border-blue-500 tw-bg-blue-50"
                                : "tw-border-gray-200 hover:tw-bg-gray-50"
                            }`}
                            onClick={() =>
                              handleRowSelected({ data: engagement } as any)
                            }
                          >
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                              <span className="tw-font-medium tw-text-gray-900 tw-capitalize">
                                {engagement.status}
                              </span>
                              <span
                                className={`tw-text-xs tw-font-medium tw-px-2 tw-py-1 tw-rounded-full ${
                                  engagement.is_current
                                    ? "tw-bg-green-100 tw-text-green-700"
                                    : "tw-bg-gray-100 tw-text-gray-700"
                                }`}
                              >
                                {engagement.is_current ? "Current" : "Closed"}
                              </span>
                            </div>
                            <div className="tw-text-xs tw-text-gray-600 tw-mt-2 tw-space-y-1">
                              <div>
                                <strong>Start:</strong>{" "}
                                {engagement.start_date || "N/A"}
                              </div>
                              <div>
                                <strong>End:</strong>{" "}
                                {engagement.end_date || "Current"}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="tw-text-sm tw-text-gray-500 tw-text-center tw-py-6">
                        No engagements found.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div
              className={
                operationMode
                  ? "tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-2"
                  : "tw-min-h-[11rem] md:tw-min-h-[16.4rem]"
              }
            >
              {error && (
                <div className="tw-p-1 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-text-red-700">
                  {error}
                </div>
              )}
              {success && (
                <div className="tw-p-1 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-text-green-700">
                  {success}
                </div>
              )}

              {operationMode ? (
                <>
                  <h3 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-2">
                    {operationMode === "edit"
                      ? "Edit Engagement"
                      : "Create Engagement"}
                  </h3>

                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-min-h-[8.5rem] md:tw-min-h-[8rem]">
                    <div className="tw-max-h-10">
                      <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-0.5">
                        Status
                      </label>
                      <select
                        className="tw-w-full tw-px-2 tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-md tw-text-xs"
                        value={formValues.status}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                      >
                        {statusOptions.map((option) => (
                          <option key={option.id} value={option.value}>
                            {option.value}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="tw-max-h-0"></div>
                    <div className="tw-max-h-10">
                      <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-0.5">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="tw-w-full tw-px-2 tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-md tw-text-xs"
                        value={formValues.start_date}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            start_date: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {operationMode === "edit" && selectedEngagement && (
                      <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-0.5">
                          End Date
                        </label>
                        <input
                          type="date"
                          className={`tw-w-full tw-px-2 tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-md tw-text-xs ${
                            isEditingCurrentEngagement
                              ? "tw-bg-gray-100 tw-text-gray-500 tw-cursor-not-allowed"
                              : ""
                          }`}
                          value={formValues.end_date || ""}
                          disabled={isEditingCurrentEngagement}
                          onChange={(e) =>
                            setFormValues((prev) => ({
                              ...prev,
                              end_date: e.target.value,
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-200">
                    <button
                      type="button"
                      onClick={() => clearOperation()}
                      className="tw-px-2 tw-py-1.5 tw-text-xs tw-text-gray-600 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                    >
                      {operationMode === "edit" ? "Cancel Edit" : "Cancel"}
                    </button>
                    <HeadlessButton
                      type="button"
                      disabled={isSaving}
                      onClick={handleSave}
                      className="md:tw-w-[30%] tw-h-8 tw-text-xs"
                    >
                      {isSaving ? (
                        <Spinner className="tw-flex tw-justify-center tw-items-center" />
                      ) : operationMode === "edit" ? (
                        "Update Status"
                      ) : (
                        "Create Status"
                      )}
                    </HeadlessButton>
                  </div>
                </>
              ) : null}
            </div>

            {isMobile && (
              <div className="tw-sticky tw-bottom-0 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-shadow-sm">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <div className="tw-text-xs tw-text-gray-600">
                    {operationMode === "edit" && selectedEngagement
                      ? `Editing: ${selectedEngagement.status}`
                      : operationMode === "create"
                      ? "Creating new engagement"
                      : ""}
                  </div>
                  <button
                    type="button"
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                    onClick={startCreateMode}
                  >
                    <MdAdd size={16} />
                    Create New
                  </button>
                </div>
              </div>
            )}
          </div>
        </ScrollableModalBody>
      )}
    </HeadlessDialog>
  );
}
